/**
 * AIP session — Supabase Auth edition.
 * Handles sign-in, sign-out, session persistence (via localStorage via supabase-js),
 * and exposes a thin API helper that attaches the current access token as a Bearer header.
 *
 * Multi-tenant: login and restoreSession now look up the user by email WITHOUT
 * a hardcoded tenant_id filter, then return the tenant row so callers can read
 * currentTenant.is_demo. attilius@mi6group.com (tenant 2) and bryan.short@mi6group.com
 * (tenant 1) both work correctly.
 */
import { createClient, type SupabaseClient, type Session } from "@supabase/supabase-js";
import * as Sentry from "@sentry/react";
import {
  getImpersonationTargetId,
  restoreImpersonationTargetFromStorage,
  setImpersonationTarget,
  clearImpersonationTarget,
  type ImpersonationTarget,
} from "./impersonation";

// ── Supabase client (browser) ──────────────────────────────────────────────
const SUPABASE_URL =
  (import.meta as any).env?.VITE_SUPABASE_URL ||
  "https://gstkgiwdjvztopjbeoiy.supabase.co";
const SUPABASE_ANON_KEY =
  (import.meta as any).env?.VITE_SUPABASE_ANON_KEY ||
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImdzdGtnaXdkanZ6dG9wamJlb2l5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzc4ODg2NTYsImV4cCI6MjA5MzQ2NDY1Nn0.ZjSvXxwNY3oO4D_xw-ORlRWNPC_0MkibW3ooU-vSeGM";

let _supabase: SupabaseClient | null = null;

export function supabase(): SupabaseClient {
  if (!_supabase) {
    _supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        persistSession: true,
        storageKey: "aip-session",
        autoRefreshToken: true,    // refresh JWT silently in the background
        detectSessionInUrl: true,  // catch magic-link / reset redirects
      },
    });
  }
  return _supabase;
}

// ── Tenant context ─────────────────────────────────────────────────────────
export interface TenantContext {
  id: number;
  name: string;
  slug: string;
  is_demo: boolean;
  is_pilot?: boolean; // T3 pilot cohort
}

let _tenant: TenantContext | null = null;

export function getCurrentTenant(): TenantContext | null {
  return _tenant;
}

export function setCurrentTenant(t: TenantContext | null) {
  _tenant = t;
}

// ── Session user (AIP users row) ───────────────────────────────────────────
export interface SessionUser {
  id: string; // Supabase auth uuid
  email: string;
  name: string;
  role: "manager" | "bdr" | "ae" | "executive" | "marketing" | "se";
  isDualRole: boolean;
  tenantId: number;
  token: string; // Supabase access_token
  mustChangePassword: boolean;
  onboardingCompletedAt: string | null; // W66.F
}

let _cached: SessionUser | null = null;

export function getSession(): SessionUser | null {
  return _cached;
}

export function setSession(s: SessionUser | null) {
  _cached = s;
  try {
    if (s) {
      Sentry.setUser({ id: s.id, email: s.email, username: s.name });
      Sentry.setTag("tenant_id", String(s.tenantId));
      Sentry.setTag("role", s.role);
    } else {
      Sentry.setUser(null);
    }
  } catch {
    // Sentry not initialised — ignore.
  }
}

export function clearSession() {
  _cached = null;
  _tenant = null;
  try {
    Sentry.setUser(null);
  } catch {
    // ignore
  }
}

// ── API helper ─────────────────────────────────────────────────────────────
const API_BASE = "__PORT_5000__".startsWith("__") ? "" : "__PORT_5000__";

export async function api<T = any>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((init.headers as Record<string, string>) || {}),
  };

  // Prefer the cached session token; fall back to current Supabase session.
  let token = _cached?.token;
  if (!token) {
    const { data } = await supabase().auth.getSession();
    token = data.session?.access_token;
  }
  if (token) headers["Authorization"] = `Bearer ${token}`;

  // D-074: when an active impersonation target is set, every API request goes
  // through with the target_user_id header. The server validates against the
  // impersonation_sessions table on every request — the header alone is never
  // trusted. Routes under /api/admin/impersonate/* skip this header so the
  // real actor is always the actor for impersonation start/stop/current.
  if (!path.startsWith("/api/admin/impersonate")) {
    const targetId = getImpersonationTargetId();
    if (targetId && !headers["X-Impersonate-User-Id"]) {
      headers["X-Impersonate-User-Id"] = targetId;
    }
  }

  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const res = await fetch(url, { ...init, headers });

  if (!res.ok) {
    let body: any = null;
    try { body = await res.json(); } catch { /* not json */ }

    // D-078 (2026-05-22): 403 self-heal. If impersonation is active locally
    // but the server-side session has been killed (manually ended or
    // expired), every request gets a 403 because the X-Impersonate-User-Id
    // header falls back to the real actor while the client still thinks
    // it's impersonating. Detect this once per error, verify with the
    // server, and if the session is gone, clear local state and reload
    // automatically. The reload is guarded so we never loop.
    if (
      res.status === 403 &&
      !path.startsWith("/api/admin/impersonate") &&
      getImpersonationTargetId() &&
      typeof window !== "undefined" &&
      !(window as any).__impersonationRecovering
    ) {
      (window as any).__impersonationRecovering = true;
      try {
        const verify = await fetch(`${API_BASE}/api/admin/impersonate/current`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (verify.ok) {
          const vdata = await verify.json().catch(() => null);
          if (vdata && vdata.active === false) {
            clearImpersonationTarget();
            window.location.reload();
            // Throw something so the caller doesn't see a successful return
            // before the reload finishes.
            throw new Error("Impersonation session ended \u2014 reloading.");
          }
        }
      } catch (e: any) {
        if (e?.message?.startsWith("Impersonation session ended")) throw e;
        // ignore verify failures; fall through to normal error
      } finally {
        // Allow future 403s to retry verification once the user re-impersonates.
        setTimeout(() => { (window as any).__impersonationRecovering = false; }, 5000);
      }
    }

    const friendly =
      res.status === 401
        ? "Your session expired. Please sign in again."
        : res.status === 403
        ? "You don\u2019t have permission to do that."
        : res.status === 404
        ? "That page or record could not be found."
        : res.status >= 500
        ? "The server hit a snag. Please try again, or contact Bryan."
        : `Request failed (${res.status}).`;
    const err: any = new Error(body?.reason || body?.error || friendly);
    err.status = res.status;
    err.body = body;
    throw err;
  }

  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) return (await res.json()) as T;
  return (await res.text()) as any as T;
}

// ── Auth actions ───────────────────────────────────────────────────────────

/**
 * Sign in with email + password via Supabase Auth.
 * Looks up the user by email WITHOUT hardcoding tenant_id, so both
 * attilius@mi6group.com (tenant 2) and bryan.short@mi6group.com (tenant 1) work.
 */
export async function login(email: string, password: string): Promise<SessionUser> {
  const sb = supabase();
  const { data: authData, error: authError } = await sb.auth.signInWithPassword({
    email,
    password,
  });
  if (authError || !authData.session) {
    throw new Error(authError?.message || "Sign in failed");
  }

  const token = authData.session.access_token;
  const authUserId = authData.user?.id;

  // Look up user row by email only (no tenant_id filter — tenant is on the row itself)
  const { data: rows, error: rowErr } = await sb
    .from("users")
    .select("id, email, full_name, role, is_dual_role, tenant_id, must_change_password, onboarding_completed_at")
    .eq("email", email.trim().toLowerCase())
    .limit(1);

  if (rowErr) {
    throw new Error(rowErr.message || "Could not load your account.");
  }
  const row = rows?.[0];
  if (!row) {
    throw new Error(
      "Signed in, but no Mi6 user record was found for this email. Contact Bryan to get added.",
    );
  }

  // Load the tenant row to get is_demo flag
  const { data: tenantRows } = await sb
    .from("tenants")
    .select("id, name, slug, is_demo, is_pilot")
    .eq("id", row.tenant_id)
    .limit(1);
  const tenantRow = tenantRows?.[0];
  if (tenantRow) {
    setCurrentTenant({
      id: tenantRow.id,
      name: tenantRow.name,
      slug: tenantRow.slug,
      is_demo: !!tenantRow.is_demo,
      is_pilot: !!(tenantRow as any).is_pilot,
    });
  }

  const user: SessionUser = {
    id: row.id || authUserId || "",
    email: row.email,
    name: (row as any).full_name || (row as any).name || row.email,
    role: row.role as SessionUser["role"],
    isDualRole: !!row.is_dual_role,
    tenantId: row.tenant_id,
    token,
    onboardingCompletedAt: (row as any).onboarding_completed_at ?? null, // W66.F
    mustChangePassword: !!(row as any).must_change_password,
  };
  setSession(user);

  // D-074: a fresh login always clears any leftover impersonation state from a
  // previous tab / device. Impersonation is a per-session intent, not a
  // permanent setting.
  clearImpersonationTarget();

  // Fire-and-forget login audit. Imported lazily to avoid a session<->activity-log cycle.
  try {
    const { logActivity } = await import("./activity-log");
    void logActivity({
      action: "auth.login",
      entity_type: "user",
      entity_id: user.id,
      payload: { role: user.role, tenant_id: user.tenantId },
    });
  } catch { /* never block sign-in on audit */ }

  return user;
}

/**
 * Send a magic-link email for passwordless sign-in.
 */
export async function sendMagicLink(email: string): Promise<void> {
  const { error } = await supabase().auth.signInWithOtp({ email });
  if (error) throw new Error(error.message);
}

/**
 * Send a password reset email. Supabase emails a link that returns to
 * /#/login?reset=1 with a recovery token in the URL hash. The Login
 * page detects a PASSWORD_RECOVERY auth event and shows the reset form.
 */
export async function sendPasswordReset(email: string): Promise<void> {
  const redirectTo = `${window.location.origin}/#/login?reset=1`;
  const { error } = await supabase().auth.resetPasswordForEmail(
    email.trim().toLowerCase(),
    { redirectTo },
  );
  if (error) throw new Error(error.message);
}

/**
 * Clear the must_change_password flag on the users row for the
 * currently signed-in user. Called after a successful first-login
 * password change.
 */
export async function clearMustChangePassword(userId: string): Promise<void> {
  const sb = supabase();
  await sb.from("users").update({ must_change_password: false }).eq("id", userId);
  if (_cached && _cached.id === userId) {
    _cached = { ..._cached, mustChangePassword: false };
  }
}

/**
 * Notification preferences mirror the user_notification_prefs row. They live
 * server-side so cron jobs can honor them when assembling Resend payloads.
 */
export interface NotificationPrefs {
  daily_digest: boolean;
  escalations: boolean;
  weekly_rollup: boolean;
  support_alerts: boolean;
}

const DEFAULT_PREFS: NotificationPrefs = {
  daily_digest: true,
  escalations: true,
  weekly_rollup: true,
  support_alerts: true,
};

export async function loadNotificationPrefs(userId: string): Promise<NotificationPrefs> {
  const sb = supabase();
  const { data, error } = await sb
    .from("user_notification_prefs")
    .select("daily_digest, escalations, weekly_rollup, support_alerts")
    .eq("user_id", userId)
    .maybeSingle();
  if (error || !data) return { ...DEFAULT_PREFS };
  return {
    daily_digest:    data.daily_digest    ?? true,
    escalations:     data.escalations     ?? true,
    weekly_rollup:   data.weekly_rollup   ?? true,
    support_alerts:  data.support_alerts  ?? true,
  };
}

export async function saveNotificationPrefs(userId: string, prefs: NotificationPrefs): Promise<void> {
  const sb = supabase();
  const row = {
    user_id: userId,
    daily_digest: prefs.daily_digest,
    escalations: prefs.escalations,
    weekly_rollup: prefs.weekly_rollup,
    support_alerts: prefs.support_alerts,
    updated_at: new Date().toISOString(),
  };
  // Upsert on user_id so cron jobs always see current preferences.
  const { error } = await sb.from("user_notification_prefs").upsert(row, { onConflict: "user_id" });
  if (error) throw new Error(error.message);
}

/**
 * Sign out from Supabase Auth and clear the local session cache.
 */
export async function logout(): Promise<void> {
  // D-074: if an impersonation session is active, end it cleanly so the audit
  // log records an end_reason of "user_exit" rather than just timing out.
  try {
    if (getImpersonationTargetId()) {
      await api("/api/admin/impersonate/stop", { method: "POST" }).catch(() => {});
    }
  } catch { /* never block logout on impersonation cleanup */ }
  clearImpersonationTarget();

  try {
    await supabase().auth.signOut();
    await api("/api/logout", { method: "POST" }).catch(() => {});
  } finally {
    clearSession();
  }
}

/**
 * Restore the session from Supabase Auth on app load.
 * No hardcoded tenant_id — reads tenant from the users row itself.
 */
export async function restoreSession(): Promise<SessionUser | null> {
  const sb = supabase();
  const { data } = await sb.auth.getSession();
  const session = data.session;
  if (!session?.access_token || !session.user?.email) return null;

  try {
    const { data: rows, error } = await sb
      .from("users")
      .select("id, email, full_name, role, is_dual_role, tenant_id, must_change_password, onboarding_completed_at")
      .eq("email", session.user.email.toLowerCase())
      .limit(1);
    if (error) return null;
    const row = rows?.[0];
    if (!row) return null;

    // Load tenant context (is_demo flag)
    const { data: tenantRows } = await sb
      .from("tenants")
      .select("id, name, slug, is_demo, is_pilot")
      .eq("id", row.tenant_id)
      .limit(1);
    const tenantRow = tenantRows?.[0];
    if (tenantRow) {
      setCurrentTenant({
        id: tenantRow.id,
        name: tenantRow.name,
        slug: tenantRow.slug,
        is_demo: !!tenantRow.is_demo,
        is_pilot: !!(tenantRow as any).is_pilot,
      });
    }

    const user: SessionUser = {
      id: row.id || session.user.id,
      email: row.email,
      name: (row as any).full_name || (row as any).name || row.email,
      role: row.role as SessionUser["role"],
      isDualRole: !!row.is_dual_role,
      tenantId: row.tenant_id,
      token: session.access_token,
      onboardingCompletedAt: (row as any).onboarding_completed_at ?? null, // W66.F
      mustChangePassword: !!(row as any).must_change_password,
    };
    setSession(user);

    // BUILD-13 Item 6: Run localStorage → Supabase backfill on first login per device.
    // Fire-and-forget — never blocks the login flow.
    import("@/lib/bdr-backfill").then(({ runBdrBackfillIfNeeded }) => {
      void runBdrBackfillIfNeeded(user.id);
    }).catch(() => { /* ignore — non-critical */ });

    // D-074: rehydrate impersonation state. Read from localStorage first so the
    // banner renders on first paint, then verify with the server. If the server
    // says there's no active session, clear local state.
    restoreImpersonationTargetFromStorage();
    verifyImpersonationWithServer().catch(() => { /* non-critical */ });

    return user;
  } catch {
    return null;
  }
}

/**
 * D-074: verify with the server that the current impersonation target is still
 * an active session (60-min window not exceeded, not manually ended). Call
 * after restoreSession() or anytime the app suspects state may be stale.
 */
export async function verifyImpersonationWithServer(): Promise<void> {
  try {
    const resp = await api<{
      active: boolean;
      session_id?: string;
      expires_at?: string;
      target?: { id: string; email: string; name: string; role: string; tenantId: number };
    }>("/api/admin/impersonate/current");
    if (resp.active && resp.target && resp.session_id) {
      const next: ImpersonationTarget = {
        id: resp.target.id,
        email: resp.target.email,
        name: resp.target.name,
        role: resp.target.role as ImpersonationTarget["role"],
        tenantId: resp.target.tenantId,
        expiresAt: resp.expires_at ?? null,
        sessionId: resp.session_id,
      };
      setImpersonationTarget(next);
    } else {
      // Server says no active session — clear any cached local value.
      clearImpersonationTarget();
    }
  } catch {
    // On error keep whatever local state we have. Worst case the banner
    // shows but no header is sent because the session row doesn't exist;
    // server will then run as the real actor and audit-log nothing extra.
  }
}
